import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilIcon, CheckCircleIcon, XCircleIcon, SpeakerWaveIcon, TrashIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '../contexts/ThemeContext';
import { Card } from '../types';
import { CARD_LIMITS, getTextLength, getCharCountStatus, validateCardContent } from '../utils/cardLimits';
import { t } from '../utils/translations';
import { ttsService } from '../utils/textToSpeech';
import toast from 'react-hot-toast';
import ImageLightbox from './ImageLightbox';

interface CardEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: Card | null;
  onSave: (front: string, back: string, difficulty: number, tags: string[], frontImage?: string, backImage?: string) => Promise<void>;
  onDelete?: () => Promise<void>;
}

const CardEditModal: React.FC<CardEditModalProps> = ({ isOpen, onClose, card, onSave, onDelete }) => {
  const { isDark } = useTheme();
  const [editedFront, setEditedFront] = useState('');
  const [editedBack, setEditedBack] = useState('');
  const [editedFrontImage, setEditedFrontImage] = useState<string | undefined>(undefined);
  const [editedBackImage, setEditedBackImage] = useState<string | undefined>(undefined);
  const [editedDifficulty, setEditedDifficulty] = useState(1);
  const [editedTags, setEditedTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<'front' | 'back' | 'details'>('front');

  // Character count tracking
  const [frontCharCount, setFrontCharCount] = useState(0);
  const [backCharCount, setBackCharCount] = useState(0);
  
  // TTS states
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentlyPlayingText, setCurrentlyPlayingText] = useState<string>('');
  
  // Error message tracking to prevent multiple toasts
  const [frontErrorShown, setFrontErrorShown] = useState(false);
  const [backErrorShown, setBackErrorShown] = useState(false);

  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // Initialize form when card changes
  useEffect(() => {
    if (card) {
      setEditedFront(card.front);
      setEditedBack(card.back);
      setEditedFrontImage(card.frontImage);
      setEditedBackImage(card.backImage);
      setEditedDifficulty(card.difficulty || 1);
      setEditedTags(card.tags || []);
      setActiveTab('front');
      
      // Update character counts
      setFrontCharCount(getTextLength(card.front));
      setBackCharCount(getTextLength(card.back));
      
      // Reset error states
      setFrontErrorShown(false);
      setBackErrorShown(false);
    }
  }, [card]);

  // Handle front content change with character counting
  const handleFrontChange = (value: string) => {
    setEditedFront(value);
    const charCount = getTextLength(value);
    setFrontCharCount(charCount);
    
    if (charCount > CARD_LIMITS.MAX_FRONT_LENGTH) {
      // Only show toast if we haven't already shown it
      if (!frontErrorShown) {
        toast.error(`Texto da pergunta excede ${CARD_LIMITS.MAX_FRONT_LENGTH} caracteres (${charCount})`);
        setFrontErrorShown(true);
      }
    } else {
      // Reset error state when back under limit
      setFrontErrorShown(false);
    }
  };

  // Handle back content change with character counting
  const handleBackChange = (value: string) => {
    setEditedBack(value);
    const charCount = getTextLength(value);
    setBackCharCount(charCount);
    
    if (charCount > CARD_LIMITS.MAX_BACK_LENGTH) {
      // Only show toast if we haven't already shown it
      if (!backErrorShown) {
        toast.error(`Texto da resposta excede ${CARD_LIMITS.MAX_BACK_LENGTH} caracteres (${charCount})`);
        setBackErrorShown(true);
      }
    } else {
      // Reset error state when back under limit
      setBackErrorShown(false);
    }
  };

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline']
    ]
  };

  const quillFormats = [
    'bold', 'italic', 'underline'
  ];

  const handleSave = async () => {
    if (!card) return;
    
    // Validate content using shared validation utility
    const validation = validateCardContent(editedFront, editedBack);
    
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(editedFront, editedBack, editedDifficulty, editedTags, editedFrontImage, editedBackImage);
      onClose();
    } catch (error) {
      console.error('Failed to save card:', error);
      toast.error('Failed to save card');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (card) {
      setEditedFront(card.front);
      setEditedBack(card.back);
      setEditedFrontImage(card.frontImage);
      setEditedBackImage(card.backImage);
      setEditedDifficulty(card.difficulty || 1);
      setEditedTags(card.tags || []);
      setFrontCharCount(getTextLength(card.front));
      setBackCharCount(getTextLength(card.back));
    }
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!card || !onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
      onClose();
      toast.success('Card excluído com sucesso!');
    } catch (error) {
      console.error('Failed to delete card:', error);
      toast.error('Falha ao excluir o card');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePlayAudio = async (text: string) => {
    if (!ttsService.getIsSupported()) {
      toast.error(t('tts.notSupported'));
      return;
    }

    try {
      // Stop any currently playing audio
      if (isSpeaking) {
        ttsService.stop();
        setIsSpeaking(false);
        setCurrentlyPlayingText('');
        return;
      }

      setIsSpeaking(true);
      setCurrentlyPlayingText(text);

      // Uses ElevenLabs with automatic language detection
      await ttsService.speak(text);

      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    } catch (error) {
      console.error('TTS Error:', error);
      toast.error(t('tts.error'));
      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    }
  };

  // Clean up TTS when component unmounts
  useEffect(() => {
    return () => {
      if (ttsService.isSpeaking()) {
        ttsService.stop();
        setIsSpeaking(false);
        setCurrentlyPlayingText('');
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={handleCancel}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="sm:flex sm:items-start">
              <div className="w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <PencilIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Editar Card
                    </h3>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="px-4 pt-4 border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('front')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'front'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('cards.front')}
              </button>
              <button
                onClick={() => setActiveTab('back')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'back'
                    ? 'border-green-500 text-green-600 dark:text-green-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('cards.back')}
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Detalhes
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-4 pt-5 pb-4 sm:p-6">
            {activeTab === 'front' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pergunta (Frente do Card)
                  </label>
                  <button
                    onClick={() => handlePlayAudio(editedFront)}
                    disabled={!editedFront.trim()}
                    className={`inline-flex items-center justify-center w-8 h-8 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                      !editedFront.trim() 
                        ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 cursor-not-allowed opacity-50' 
                        : isSpeaking && currentlyPlayingText === editedFront
                          ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                    }`}
                    title={isSpeaking ? t('tts.stopAudio') : t('tts.playAudio')}
                  >
                    <SpeakerWaveIcon className={`h-4 w-4 ${isSpeaking && currentlyPlayingText === editedFront ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
                <ReactQuill
                  key="front-editor"
                  value={editedFront}
                  onChange={handleFrontChange}
                  modules={quillModules}
                  formats={quillFormats}
                  className={`${isDark ? 'dark-quill' : ''}`}
                  theme="snow"
                  placeholder="Digite a pergunta..."
                  style={{
                    backgroundColor: isDark ? '#374151' : '#ffffff',
                    color: isDark ? '#ffffff' : '#000000',
                    minHeight: '200px'
                  }}
                />
                <div className="flex justify-between items-center mt-1">
                  <div className={`text-xs ${
                    frontCharCount > CARD_LIMITS.MAX_FRONT_LENGTH 
                      ? 'text-red-600 dark:text-red-400 font-medium' 
                      : frontCharCount > CARD_LIMITS.MAX_FRONT_LENGTH * 0.8 
                        ? 'text-amber-600 dark:text-amber-400' 
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {frontCharCount} / {CARD_LIMITS.MAX_FRONT_LENGTH} caracteres
                  </div>
                  {frontCharCount > CARD_LIMITS.MAX_FRONT_LENGTH && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Excesso: {frontCharCount - CARD_LIMITS.MAX_FRONT_LENGTH}
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'back' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Resposta (Verso do Card)
                  </label>
                  <button
                    onClick={() => handlePlayAudio(editedBack)}
                    disabled={!editedBack.trim()}
                    className={`inline-flex items-center justify-center w-8 h-8 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                      !editedBack.trim() 
                        ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 cursor-not-allowed opacity-50' 
                        : isSpeaking && currentlyPlayingText === editedBack
                          ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                    }`}
                    title={isSpeaking ? t('tts.stopAudio') : t('tts.playAudio')}
                  >
                    <SpeakerWaveIcon className={`h-4 w-4 ${isSpeaking && currentlyPlayingText === editedBack ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
                <ReactQuill
                  key="back-editor"
                  value={editedBack}
                  onChange={handleBackChange}
                  modules={quillModules}
                  formats={quillFormats}
                  className={`${isDark ? 'dark-quill' : ''}`}
                  theme="snow"
                  placeholder="Digite a resposta..."
                  style={{
                    backgroundColor: isDark ? '#374151' : '#ffffff',
                    color: isDark ? '#ffffff' : '#000000',
                    minHeight: '200px'
                  }}
                />
                <div className="flex justify-between items-center mt-1">
                  <div className={`text-xs ${
                    backCharCount > CARD_LIMITS.MAX_BACK_LENGTH 
                      ? 'text-red-600 dark:text-red-400 font-medium' 
                      : backCharCount > CARD_LIMITS.MAX_BACK_LENGTH * 0.8 
                        ? 'text-amber-600 dark:text-amber-400' 
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {backCharCount} / {CARD_LIMITS.MAX_BACK_LENGTH} caracteres
                  </div>
                  {backCharCount > CARD_LIMITS.MAX_BACK_LENGTH && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Excesso: {backCharCount - CARD_LIMITS.MAX_BACK_LENGTH}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Difficulty Selection */}
                <div>
                  <label htmlFor="edit-difficulty" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('cards.difficulty')}
                  </label>
                  <select
                    id="edit-difficulty"
                    value={editedDifficulty}
                    onChange={(e) => setEditedDifficulty(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value={1}>{t('cards.difficulties.1')}</option>
                    <option value={2}>{t('cards.difficulties.2')}</option>
                    <option value={3}>{t('cards.difficulties.3')}</option>
                    <option value={4}>{t('cards.difficulties.4')}</option>
                    <option value={5}>{t('cards.difficulties.5')}</option>
                  </select>
                </div>

                {/* Tags Input */}
                <div>
                  <label htmlFor="edit-tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('cards.tags')} ({t('cards.optional')})
                  </label>
                  <input
                    type="text"
                    id="edit-tags"
                    value={editedTags.join(', ')}
                    onChange={(e) => {
                      const tags = e.target.value
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0);
                      setEditedTags(tags);
                    }}
                    placeholder={t('cards.tagPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Separe as tags com vírgulas (ex: vocabulário, básico, verbo)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 sm:px-6 bg-gray-50 dark:bg-gray-700">
            {showDeleteConfirm ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                  Tem certeza que deseja excluir este card? Esta ação não pode ser desfeita.
                </p>
                <div className="flex justify-center space-x-3">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    {isDeleting ? 'Excluindo...' : 'Sim, Excluir'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                    className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="sm:flex sm:flex-row sm:justify-between sm:gap-4">
                <div className="sm:flex sm:flex-row-reverse sm:flex-1 sm:gap-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed dark:bg-green-700 dark:hover:bg-green-600"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    {isSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-0 sm:w-auto sm:text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Cancelar
                  </button>
                </div>
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isSaving}
                    className="mt-3 w-full inline-flex justify-center items-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-600 hover:text-red-600 hover:border-red-400 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 sm:mt-0 sm:w-auto dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:text-red-400 dark:hover:border-red-500 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Excluir
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage}
        onClose={() => setLightboxOpen(false)}
        alt="Card Image"
      />
    </div>
  );
};

export default CardEditModal;